import { FormInstance } from "antd";
import { isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseFormSnapshotProps<T> {
    form: FormInstance;
    getSnapshot: () => T;
    onStateChange?: (isChanged: boolean) => void;
}

export const useFormSnapshot = <T,>({ form, getSnapshot, onStateChange }: UseFormSnapshotProps<T>) => {
    const initialSnapshotRef = useRef<T | null>(null);

    const hasCommittedRef = useRef(false);
    const [isChange, setIsChangeInternal] = useState(false);

    const getSnapshotRef = useRef(getSnapshot);
    const onStateChangeRef = useRef(onStateChange);

    useEffect(() => {
        getSnapshotRef.current = getSnapshot;
    }, [getSnapshot]);

    useEffect(() => {
        onStateChangeRef.current = onStateChange;
    }, [onStateChange]);

    const checkChange = useCallback(() => {

        if (!hasCommittedRef.current) return;

        const currentSnapshot = getSnapshotRef.current();
        const hasChanged = !isEqual(currentSnapshot, initialSnapshotRef.current);




        setIsChangeInternal((prev) => {
            if (prev !== hasChanged) {
                if (onStateChangeRef.current) onStateChangeRef.current(hasChanged);
                return hasChanged;
            }
            return prev;
        });
    }, []);

    const commitSnapshot = useCallback(() => {
        initialSnapshotRef.current = getSnapshotRef.current();

        hasCommittedRef.current = true;
        setIsChangeInternal(false);
        if (onStateChangeRef.current) onStateChangeRef.current(false);
    }, []);

    // Call when navigating away without saving (e.g. switching to another record).
    // Prevents stale snapshot from triggering false dirty state on the new record.
    const resetSnapshot = useCallback(() => {
        initialSnapshotRef.current = null;
        hasCommittedRef.current = false;
        setIsChangeInternal(false);
        if (onStateChangeRef.current) onStateChangeRef.current(false);
    }, []);

    return {
        isChange,
        setIsChange: setIsChangeInternal,
        checkChange,
        commitSnapshot,
        resetSnapshot,
    };
};